// Spec: ./EditImageModal.spec.md
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ImageMember } from 'api-client/Glance'
import { vtpmDoc } from 'app/docLinks'
import { useAppSelector } from 'app/store'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import ExternalLink from 'core/components/ExternalLink'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import RadioFields from 'core/components/validatedForm/radio-fields'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isAdminRole } from 'core/utils/rbac'
import { noop } from 'utils/fp'
import { vtpmRadioOptions } from './AddImageModal'
import { listImages, updateImage } from './actions'
import {
  addImageMemberAction,
  imageMembersSelector,
  listImageMembersAction,
  removeImageMemberAction,
} from './imageMembers'
import TenantAccessField from './TenantAccessField'
import VisibilityPicklist from './VisibilityPicklist'

interface JsonPatchOp {
  op: 'add' | 'replace' | 'remove'
  path: string
  value?: string | boolean
}

interface EditableImage {
  id: string
  name: string
  visibility: string
  owner: string
  protected: boolean
  hw_tpm_version?: string
  hw_tpm_model?: string
  hw_rescue_device?: string
  hw_rescue_bus?: string
  [key: string]: unknown
}

interface Props {
  rows: EditableImage[]
  onClose: () => void
}

const buildMetadataOps = (
  imageKeys: string[],
  enabled: boolean,
  key: string,
  value: string,
): JsonPatchOp[] => {
  const ops: JsonPatchOp[] = []
  if (enabled) {
    if (imageKeys.includes(key)) {
      ops.push({
        op: 'replace',
        path: `/${key}`,
        value,
      })
    } else {
      ops.push({
        op: 'add',
        path: `/${key}`,
        value,
      })
    }
  } else {
    if (imageKeys.includes(key)) {
      ops.push({
        op: 'remove',
        path: `/${key}`,
      })
    }
  }
  return ops
}

export default function EditImageModal({ rows: [image], onClose }: Props) {
  const getInitialVisibility = () => {
    const v = image.visibility
    if (v === 'community') return 'community'
    if (v === 'shared') return 'shared'
    if (v === 'public') return 'public'
    return 'private'
  }

  const defaultParams = {
    name: '',
    visibility: getInitialVisibility(),
    selectedTenants: [] as string[],
    protected: false,
    enableVTPM: false,
    vtpmModel: 'tpm-tis',
    enableRescue: false,
  }

  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  // Use a ref to avoid React state timing issues when re-triggering submit
  const confirmationRef = useRef(false)
  // Track whether we've already seeded selectedTenants from the API response
  const membersInitializedRef = useRef(false)

  useEffect(() => {
    updateParams({
      name: image.name,
      visibility: getInitialVisibility(),
      protected: image.protected,
      enableVTPM: image?.hw_tpm_version === '2.0',
      vtpmModel: image?.hw_tpm_model || 'tpm-tis',
      enableRescue: image?.hw_rescue_device === 'disk' && image?.hw_rescue_bus === 'scsi',
    })
  }, [])

  // Load existing image members (only relevant for shared images)
  const { loading: loadingMembers, reload: reloadMembers } = useListAction(listImageMembersAction, {
    params: { imageId: image.id },
  })
  const existingMembers = useAppSelector((state) =>
    imageMembersSelector(state, { imageId: image.id }),
  ) as ImageMember[]

  // Pre-populate selectedTenants once members load (shared images only), never again
  useEffect(() => {
    if (!loadingMembers && existingMembers?.length > 0 && !membersInitializedRef.current) {
      membersInitializedRef.current = true
      updateParams({ selectedTenants: existingMembers.map((m) => m.member_id) })
    }
  }, [loadingMembers, existingMembers])

  const { update, updating, error, reset } = useUpdateAction(updateImage)
  const { update: addMember, error: addMemberError } = useUpdateAction(addImageMemberAction)
  const { update: removeMember, error: removeError } = useUpdateAction(removeImageMemberAction)
  const { reload } = useListAction(listImages)

  const session = useAppSelector((state) => state[sessionStoreKey]) as SessionState
  const isAdmin = useMemo(() => isAdminRole(session), [session])
  const isProtected = image?.protected

  const { prefs } = useScopedPreferences()
  const currentProjectId = prefs.currentTenant
  const isImageOwner = image?.owner === currentProjectId || isAdmin
  const cannotEdit = !isAdmin && !isImageOwner
  // PCD-8207: the image owner (not just admins) may manage sharing on their own image
  const canManageSharing = isAdmin || isImageOwner
  // PCD-8221: show the tenant panel based on the visibility the user has selected, not
  // the image's stored value — otherwise re-sharing a previously-private image never
  // reveals the panel (Glance keeps the member list across visibility changes)
  const showTenantAccess = canManageSharing && params.visibility === 'shared'

  // Visibility is shown read-only (not as an editable picklist) when the picklist
  // can't safely represent the current value for this user: community images are never
  // editable, and a non-admin must not get an editable picklist for a public image —
  // the picklist only offers private/shared, so editing would silently downgrade it.
  const visibilityReadOnly =
    image.visibility === 'community' || (image.visibility === 'public' && !isAdmin)
  const readOnlyVisibilityLabel =
    image.visibility === 'community' ? 'Community (read-only)' : 'Public (read-only)'

  // Load existing members only for shared images. Glance rejects member listing on
  // non-shared images ("Only shared images have members"), so loading for private
  // images would just produce a 403 with nothing to show.
  useEffect(() => {
    if (image.visibility === 'shared') {
      reloadMembers(true, true)
    }
  }, [])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    onClose()
  }

  const submitForm = useCallback(async () => {
    // If changing away from shared with existing members, require confirmation first
    if (
      canManageSharing &&
      image.visibility === 'shared' &&
      params.visibility !== 'shared' &&
      existingMembers?.length > 0 &&
      !confirmationRef.current
    ) {
      setShowConfirmDialog(true)
      return
    }

    // Determine the visibility value to patch. When visibility is shown read-only
    // (community, or public for a non-admin) the picklist is absent, so we patch the
    // image's original value rather than params — this guarantees we never silently
    // change a visibility the user was not allowed to edit.
    const visibilityValue = visibilityReadOnly ? image.visibility : params.visibility

    const baseOperations = [
      {
        op: 'replace',
        path: '/name',
        value: params.name,
      },
      {
        op: 'replace',
        path: '/visibility',
        value: visibilityValue,
      },
      {
        op: 'replace',
        path: '/protected',
        value: params.protected,
      },
    ]

    const imageKeys = Object.keys(image)
    const conditionalOperations: JsonPatchOp[] = []

    // vTPM version
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableVTPM, 'hw_tpm_version', '2.0'),
    )

    // vTPM model
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableVTPM, 'hw_tpm_model', params.vtpmModel),
    )

    // Rescue metadata
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableRescue, 'hw_rescue_device', 'disk'),
    )
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableRescue, 'hw_rescue_bus', 'scsi'),
    )

    const body = [...baseOperations, ...conditionalOperations]
    const { success } = await update({ id: image.id, body })
    if (!success) return

    // Member management after successful patch — owner or admin can add/remove members
    if (canManageSharing) {
      if (confirmationRef.current && params.visibility !== 'shared') {
        // Confirmed revoke: remove ALL existing members after visibility change
        for (const member of existingMembers || []) {
          const result = await removeMember({ imageId: image.id, memberId: member.member_id })
          if (!result?.success) {
            const status = result?.error?.response?.status || result?.error?.err?.response?.status
            if (status === 404 || status === 403) continue
          }
        }
      } else if (params.visibility === 'shared') {
        // Normal diff: add new members, remove deleted ones
        const originalIds = (existingMembers || []).map((m) => m.member_id)
        const toAdd = params.selectedTenants.filter((id) => !originalIds.includes(id))
        const toRemove = originalIds.filter((id) => !params.selectedTenants.includes(id))

        for (const projectId of toAdd) {
          const result = await addMember({ imageId: image.id, projectId })
          if (!result?.success) {
            const status = result?.error?.response?.status || result?.error?.err?.response?.status
            if (status === 409) continue // already a member — skip
          }
        }
        for (const memberId of toRemove) {
          const result = await removeMember({ imageId: image.id, memberId })
          if (!result?.success) {
            const status = result?.error?.response?.status || result?.error?.err?.response?.status
            if (status === 404 || status === 403) continue
          }
        }
      }
    }

    confirmationRef.current = false
    reload(true)
    handleClose()
  }, [
    image?.id,
    image?.visibility,
    params,
    existingMembers,
    canManageSharing,
    update,
    addMember,
    removeMember,
    reload,
  ])

  const handleConfirmedSubmit = useCallback(() => {
    setShowConfirmDialog(false)
    confirmationRef.current = true
    submitForm()
  }, [submitForm])

  return (
    <>
      <ModalForm
        title={`Edit Image`}
        onSubmit={submitForm}
        onClose={handleClose}
        submitting={updating}
        error={error || addMemberError || removeError}
        submitTitle={`Update Image`}
        disableSubmit={cannotEdit}
        disableSubmitMessage="Only the image owner can edit this image"
        maxWidth={600}
        open
      >
        <>
          <FormFieldSection title="Image Settings">
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              required
            />
            {canManageSharing &&
              (visibilityReadOnly ? (
                <TextField
                  id="visibility"
                  label="Visibility"
                  value={readOnlyVisibilityLabel}
                  disabled
                />
              ) : (
                <VisibilityPicklist
                  id="visibility"
                  value={params.visibility}
                  onChange={getParamsUpdater('visibility')}
                />
              ))}
            {showTenantAccess && (
              <TenantAccessField
                value={params.selectedTenants}
                onChange={getParamsUpdater('selectedTenants')}
                loading={loadingMembers}
                imageOwnerId={image?.owner}
                isAdmin={isAdmin}
              />
            )}
            <CheckboxField
              id="protected"
              label="Disable Delete (Protected)"
              onChange={!isAdmin && isProtected ? noop : getParamsUpdater('protected')}
              value={params.protected}
              disabled={!isAdmin && isProtected}
              info="Images that are marked as 'protected' can not be deleted."
              tooltipOffset={{
                vertical: 0,
                horizontal: -180,
              }}
            />
            <CheckboxField
              id="enableRescue"
              label="Use for rescuing VMs"
              onChange={getParamsUpdater('enableRescue')}
              value={params.enableRescue}
              info="When Use for rescuing VMs is enabled, this image will be used for VM rescue operations."
              tooltipOffset={{
                vertical: 0,
                horizontal: -180,
              }}
            />
            <CheckboxField
              id="enableVTPM"
              label="Enable Virtual TPM 2.0"
              onChange={getParamsUpdater('enableVTPM')}
              value={params.enableVTPM}
              info={
                <>
                  Enable vTPM for virtual machines created using this image. Read{' '}
                  <ExternalLink url={vtpmDoc}>here</ExternalLink> for more info
                </>
              }
              tooltipOffset={{
                vertical: 0,
                horizontal: -180,
              }}
            />
            {params.enableVTPM && (
              <RadioFields
                title="Virtual TPM Model"
                id="vtpmModel"
                options={vtpmRadioOptions}
                value={params.vtpmModel}
                onChange={getParamsUpdater('vtpmModel')}
              />
            )}
          </FormFieldSection>
        </>
      </ModalForm>
      {showConfirmDialog && (
        <ConfirmationDialog
          open
          title="Revoke Shared Access?"
          text={`Changing visibility to ${params.visibility} will revoke shared access for ${
            existingMembers?.length ?? 0
          } project(s). They will no longer be able to use this image. Continue?`}
          onConfirm={handleConfirmedSubmit}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}
    </>
  )
}
